/**
 * mastering-stream.js — the mastering render as a stream (window.MasteringStream)
 *
 * The rescue lane for files the buffered chain cannot hold: the chain's
 * working set is ~6× the decoded audio (906 MB on a 10-minute track, +90 MB
 * per minute — measured 2026-07-31), so the decoder's Tier-0 memory probe
 * refuses long files outright on the machines that actually report failures.
 * This lane masters the same file in O(chunk) memory: audio is decoded in
 * chunks, every DSP stage carries its state across chunk boundaries, and the
 * result goes straight to an OPFS-backed sink as WAV — no full-length buffer
 * ever exists.
 *
 * It is the SAME mastering, not a lite one:
 *   - tone EQ / glue compressor / width are 1:1 ports of the chain's stages
 *     with cross-chunk state (the compressor is the compressor-worklet's
 *     per-sample core verbatim — same soft knee, ln9 ballistics, 85 Hz
 *     side-chain HPF, 3 ms look-ahead);
 *   - the limiter is MasteringLimiter.limit() run block-by-block with one
 *     release time of context each side — measured bit-identical to the
 *     whole-file render (limiter-stream-block.mjs);
 *   - loudness is TWO EXACT R128 passes (LoudnessR128.createIntegratedMeter),
 *     because a stratified excerpt was measured lying by up to 2 LU on real
 *     material — there is no cheap shortcut, so none is taken.
 *
 * Inputs: WAV and FLAC (clean chunk seams — WAV is raw PCM, FLAC frames are
 * self-contained and the chunker verifies boundaries by header CRC-8). MP3 is
 * deliberately NOT routed here: mp3-chunked-decode slices frames without the
 * bit-reservoir back-references, and mastering-grade seam continuity for it
 * has never been measured.
 *
 * Pass structure (decode runs once per pass; ~2-3× the CPU of the buffered
 * chain, in exchange for flat memory):
 *   1 analyze — raw "before" metrics (also yields the RMS the compressor
 *     threshold is derived from, exactly as the chain measures first);
 *   2 tone — tone chain, exact integrated LUFS of what the limiter will see;
 *   3 render — tone chain again (fresh state), pre-gain, streamed limiter,
 *     exact "after" metrics, WAV to sink;
 *   4 (rare) one full correction render if the loudness landed > 0.5 LU off
 *     and the limiter is not already crushing; then a cheap output-only
 *     rescale if the measured true peak still tops the promised ceiling.
 *
 * API:
 *   MasteringStream.probe(file)        → Promise<{kind,sampleRate,channels,
 *                                         durationSec,totalSamples,bits}>
 *   MasteringStream.canHandle(file)    → {eligible, reason}     (cheap, sync)
 *   MasteringStream.master(file, {
 *     targetLufs, ceilingDb, style, width?, bits=16,
 *     onStage?, onProgress?, signal?
 *   }) → Promise<{blob, before, after, appliedGainDb, residualErrLu,
 *        overLimited, maxGrDb, iterations, peaks:{before,after,samplesPerBin},
 *        sampleRate, channels, durationSec, mode:'streaming'}>
 */
(function () {
    'use strict';
    if (window.MasteringStream) return;

    function err(msg) { var e = new Error('MasteringStream: ' + msg); e.name = 'MasteringStreamError'; return e; }
    function abortCheck(signal) {
        if (signal && signal.aborted) {
            var e = new Error('MasteringStream aborted'); e.name = 'AbortError'; throw e;
        }
    }
    function dbToLin(db) { return Math.pow(10, db / 20); }

    function deps() {
        var ASP = window.AudioStreamPipeline;
        if (!ASP || !ASP._internal || !ASP._internal.decodeWavStream) throw err('AudioStreamPipeline (with wav I/O) not loaded');
        if (!window.LoudnessR128 || !window.LoudnessR128.createIntegratedMeter) throw err('LoudnessR128.createIntegratedMeter not loaded');
        if (!window.MasteringLimiter || !window.MasteringLimiter.createStreamLimiter) throw err('MasteringLimiter.createStreamLimiter not loaded');
        if (!window.MasteringChain || !window.MasteringChain.STYLES) throw err('MasteringChain not loaded');
        if (!window.OutputSink) throw err('OutputSink not loaded');
        return ASP._internal;
    }

    // ── probe ─────────────────────────────────────────────────────────
    // Decides by MAGIC BYTES, not extension — a renamed file must route by
    // what it is. FLAC's STREAMINFO is the first metadata block by spec, so
    // 64 KB is always enough for it; WAV headers with broadcast/JUNK chunks
    // can push `data` further out, so WAV gets a wider window.
    /* Источник — ЛИБО файл, ЛИБО уже декодированный AudioBuffer.
       Второй вход появился 2026-08-05 и закрывает главную дыру этого лейна:
       по формату он брал только WAV/FLAC, то есть 43.7% загрузок мастеринга,
       тогда как ПОЛОВИНА реальных падений по памяти приходила с MP3. При этом
       декод у таких людей проходит — падает следующий шаг, буферная цепочка
       (замерено: её рабочий набор ×2.5–3.5 от декодированного ПЛЮС сам
       источник). Поэтому декодированный буфер можно прогнать через ровно тот
       же потоковый DSP: формат больше не при чём, а пик перестаёт зависеть от
       длительности. Ни одна стадия обработки для этого не переписана —
       различие живёт целиком в decodePass. */
    function isAudioBuffer(x) {
        return !!x && typeof x.getChannelData === 'function'
            && typeof x.numberOfChannels === 'number' && typeof x.length === 'number';
    }
    function canHandle(input) {
        if (!input) return { eligible: false, reason: 'no_file' };
        if (isAudioBuffer(input)) return { eligible: true, reason: 'ok_buffer' };
        var name = (input.name || '').toLowerCase();
        var type = (input.type || '').toLowerCase();
        var wav = name.slice(-4) === '.wav' || type === 'audio/wav' || type === 'audio/x-wav' || type === 'audio/wave';
        var flac = name.slice(-5) === '.flac' || type === 'audio/flac' || type === 'audio/x-flac';
        if (!wav && !flac) return { eligible: false, reason: 'format_unsupported' };
        return { eligible: true, reason: 'ok' };
    }

    async function probe(input) {
        if (isAudioBuffer(input)) {
            return {
                kind: 'buffer', sampleRate: input.sampleRate, channels: input.numberOfChannels,
                bits: 32, totalSamples: input.length,
                durationSec: input.sampleRate ? input.length / input.sampleRate : 0,
                buffer: input
            };
        }
        var I = deps();
        var head = new Uint8Array(await input.slice(0, 262144).arrayBuffer());
        var file = input;
        if (head.length >= 4 && head[0] === 0x66 && head[1] === 0x4c && head[2] === 0x61 && head[3] === 0x43) {
            if (!window.FlacChunkedDecode) throw err('FlacChunkedDecode not loaded');
            var fh = window.FlacChunkedDecode.parseHeader(head);
            return {
                kind: 'flac', sampleRate: fh.sampleRate, channels: fh.channels,
                bits: fh.bitsPerSample, totalSamples: fh.totalSamples,
                durationSec: fh.durationSec
            };
        }
        if (head.length >= 4 && head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46) {
            var wh = I.readWavHeader(head);
            var frames = Math.floor(wh.dataSize / (wh.bitsPerSample / 8 * wh.numChannels));
            return {
                kind: 'wav', sampleRate: wh.sampleRate, channels: wh.numChannels,
                bits: wh.bitsPerSample, totalSamples: frames,
                durationSec: wh.sampleRate ? frames / wh.sampleRate : 0
            };
        }
        throw err('neither RIFF nor fLaC magic — streaming route takes WAV/FLAC only');
    }

    // ── stateful ports of the chain's tone stages ─────────────────────

    // The chain's HPF/EQ run on native BiquadFilterNode. For lowpass/highpass
    // Web Audio interprets Q IN DECIBELS ("not a traditional Q, but a
    // resonance value in dB") — the trap that once made two DIFFERENT filters
    // get compared (−21 dB, audible). MEASURED against the real node
    // (2026-07-31, music-mastering-stream-equivalence.mjs): the native
    // highpass at Q.value=0.707 matches RBJ with Q_lin = 10^(0.707/20) at
    // −119 dB, while the spec-formula-looking 10^(Q/40) reading is −43 dB
    // off — a different filter. So: /20, pinned by that test forever.
    function webAudioHpLpQ(qDb) { return Math.pow(10, qDb / 20); }

    function buildToneChain(sr, nCh, styleObj, rmsDb) {
        var I = deps();
        var stages = [];
        var eq = styleObj.eq || {};
        stages.push(I.makeBiquad('highpass', sr, eq.hpf || 20, webAudioHpLpQ(0.707), 0, nCh));
        // Тембр по ходу трека — то же место, что в буферном пути: после HPF, до
        // эталона. Стадия считает сэмплы от начала трека сама, поэтому каждый
        // проход строит её заново (как и весь тон).
        if (styleObj._drift && window.MasteringTonalDrift) stages.push(window.MasteringTonalDrift.makeStage(sr, nCh, styleObj._drift));
        /* Подгонка под эталон. Здесь она стоит РОВНО там же, где в буферном пути
           (mastering-chain.js: сразу после HPF и ДО вкусовых правок), и с теми же
           правилами пропуска и добротности: усиления в MasteringReference решены
           для этого конкретного каскада, где перекрывающиеся фильтры складываются
           в дБ. Сдвинешь порядок — применённая кривая перестанет быть вычисленной.
           До 2026-08-05 этой ветки не было, и переход на потоковую дорожку молча
           терял эталон; теперь теряться нечему — источник декодирован, спектр
           считает та же MasteringReference. */
        var rb = styleObj._refBands;
        if (rb && rb.length) {
            for (var ri = 0; ri < rb.length; ri++) {
                if (Math.abs(rb[ri].gainDb) < 0.3) continue;
                stages.push(I.makeBiquad(rb[ri].type || 'peaking', sr, rb[ri].freq,
                    rb[ri].q || 1.0, rb[ri].gainDb, nCh));
            }
        }
        // Вкусовая правка пользователя — те же три фильтра и на том же месте
        // цепочки, что в буферном пути (mastering-chain.js): длинный файл не
        // должен молча игнорировать ползунки, которые человек только что двигал.
        var tone = styleObj._tone || {};
        if (tone.low)      stages.push(I.makeBiquad('lowshelf',  sr, 100,   0,   tone.low,      nCh));
        // Частота и добротность — из общей таблицы буферного пути: presence
        // обязан звучать одинаково на коротком и длинном файле.
        if (tone.presence) stages.push(I.makeBiquad('peaking', sr,
            window.MasteringChain.TONE_PRESENCE_HZ, window.MasteringChain.TONE_PRESENCE_Q, tone.presence, nCh));
        if (tone.air)      stages.push(I.makeBiquad('highshelf', sr, 11000, 0,   tone.air,      nCh));
        if (eq.lowShelf)  stages.push(I.makeBiquad('lowshelf',  sr, eq.lowShelf[0],  0, eq.lowShelf[1],  nCh));
        if (eq.harsh)     stages.push(I.makeBiquad('peaking',   sr, eq.harsh[0],     eq.harsh[2] || 1.2, eq.harsh[1], nCh));
        if (eq.presence)  stages.push(I.makeBiquad('peaking',   sr, eq.presence[0],  eq.presence[2] || 1.0, eq.presence[1], nCh));
        if (eq.highShelf) stages.push(I.makeBiquad('highshelf', sr, eq.highShelf[0], 0, eq.highShelf[1], nCh));
        var C = styleObj.comp || {};
        stages.push(makeGlueCompressor(sr, nCh, {
            thresholdDb: (isFinite(rmsDb) ? rmsDb : -18) + (C.thr != null ? C.thr : 6),
            ratio: C.ratio || 1.6,
            kneeDb: 8,
            attackMs: C.atk != null ? C.atk : 30,
            releaseMs: C.rel != null ? C.rel : 160,
            makeupDb: 0,
            lookaheadSamples: Math.round(sr * 0.003)
        }));
        /* Насыщение — ПОСЛЕ склейки, как в буферном пути (mastering-chain.js:
           компрессор не должен реагировать на гармоники, которые exciter сам
           создал). До 09.10 здесь оно стояло перед стилевым EQ и компрессором —
           два пути одного тула звучали по-разному. */
        if (tone.saturation) stages.push(makeSaturator(sr, nCh, tone.saturation));
        var w = styleObj.width || 1.0;
        if (nCh >= 2 && Math.abs(w - 1) >= 1e-3) stages.push(makeWidthStage(sr, w));
        var latency = 0;
        for (var li = 0; li < stages.length; li++) latency += stages[li].latency || 0;
        return {
            // На сколько отсчётов цепь задерживает звук (упреждение компрессора,
            // фильтры передискретизации насыщения) — renderPass отрезает это с головы.
            latency: latency,
            process: function (chans) {
                for (var i = 0; i < stages.length; i++) chans = stages[i].process(chans);
                return chans;
            }
        };
    }

    /* Гармоническое насыщение для потоковой дорожки.

       В буферном пути это делает WaveShaperNode с `oversample='4x'`: он сам
       поднимает частоту дискретизации, поэтому зеркал нет. Здесь Web Audio нет,
       и путь к правильному ответу был через два ЗАМЕРА, а не через рассуждение:

       1. Без передискретизации, только зажав полосу входа: на тоне 5 кГц при
          44.1 кГц зеркало пятой гармоники село на 19.1 кГц с уровнем −36 дБ
          (буферный путь: −147 дБ). Полосой это не лечится — пятая гармоника
          уходит за Найквист уже с 4.4 кГц, а такой узкий exciter бессмыслен.
       2. Через общий полифазный ресемплер пайплайна стало ХУЖЕ (−38 дБ): у него
          частота среза `0.9·min(1/L,1/M)` — вдвое шире правильной `0.5/L`, и
          на отношении 1:4 он пропускает образы, которые кривая затем и
          заворачивает обратно. (Для RNNoise, где L≈M, это почти незаметно —
          потому и не всплывало. Отдельная находка, чинится не здесь.)

       Поэтому здесь свой 4× с ПРАВИЛЬНЫМ срезом: окно Кайзера, 24 отвода на
       фазу, срез 0.45/4 от частоты промежуточной сетки. Кривая и пропорции —
       общие с буферным путём (MasteringChain.satDrive / satMix): «насыщенность
       40%» обязана звучать одинаково, откуда бы её ни позвали. */
    var OS = 4, OS_TAPS = 24, OS_LEN = OS * OS_TAPS;
    /* Задержка мокрого пути в отсчётах исходной частоты: два ФНЧ по OS_LEN
       отводов на сетке ×OS (по (OS_LEN−1)/2 каждый) и съём фазы OS−1 →
       (OS_LEN − OS) / OS = 23. Сухой путь задерживается на столько же: без
       этого сумма «сухой + мокрый на 23 отсчёта позже» была гребёнкой с шагом
       ~1.9 кГц (−33.5…+6.3 дБ в 3–6 кГц при 50 %, замер 09.10). */
    var SAT_DELAY = (OS_LEN - OS) / OS;
    /* Один и тот же ФНЧ работает дважды: вверх (интерполяция, полифазно — по
       одной фазе на выходной отсчёт) и вниз (децимация — полной свёрткой, иначе
       давятся не все образы; первая версия свернула только одну фазу и дала
       зеркала −31 дБ вместо −100). Срез 0.45/OS от промежуточной сетки —
       ПРАВИЛЬНЫЙ анти-образный, а не 0.9/OS общего ресемплера пайплайна. */
    var _osCache = null;
    function osKernel(I) {
        if (_osCache) return _osCache;
        var n = OS_LEN;
        var sinc = I.designLowpassSinc(n, 0.45 / OS);
        var win = I.kaiserWindow(n, 8.6);
        var h = new Float64Array(n), sum = 0, i;
        for (i = 0; i < n; i++) { h[i] = sinc[i] * win[i]; sum += h[i]; }
        for (i = 0; i < n; i++) h[i] /= sum;              // единичное усиление по DC
        var phases = [];
        for (var p = 0; p < OS; p++) {
            var ph = new Float64Array(OS_TAPS);
            for (var k = 0; k < OS_TAPS; k++) ph[k] = h[k * OS + p] * OS;   // компенсация нулей
            phases.push(ph);
        }
        _osCache = { h: h, phases: phases };
        return _osCache;
    }
    function makeSaturator(sr, nCh, amount) {
        var I = deps();
        var MC = window.MasteringChain;
        var k = MC.satDrive(amount), mix = MC.satMix(amount), t = Math.tanh(k);
        // ⚠️ Q у нативного highpass — В ДЕЦИБЕЛАХ (та же ловушка, что описана
        // выше у buildToneChain): буферный путь ставит Q.value = 0.7071, значит
        // здесь нужен webAudioHpLpQ(0.7071), а не голое 0.7071. С голым в шейпер
        // приходила другая полоса, и гармоники двух путей расходились до 6.5 дБ.
        var hp = I.makeBiquad('highpass', sr, MC.SAT_SPLIT_HZ, webAudioHpLpQ(0.7071), 0, nCh);
        var ker = osKernel(I), h = ker.h, phases = ker.phases;
        var st = [];
        for (var c = 0; c < nCh; c++) {
            st.push({ inLine: new Float64Array(OS_TAPS), hi: new Float64Array(OS_LEN), hiPos: 0, dry: new Float32Array(SAT_DELAY), dryPos: 0 });
        }
        return {
            latency: SAT_DELAY,
            process: function (chans) {
                var n = chans[0].length, c, i, p, tap;
                var band = [];
                for (c = 0; c < chans.length; c++) band.push(new Float32Array(chans[c].subarray(0, n)));
                hp.process(band);
                for (c = 0; c < chans.length; c++) {
                    var s = st[c], b = band[c], d = chans[c];
                    var inLine = s.inLine, hi = s.hi, hiPos = s.hiPos;
                    for (i = 0; i < n; i++) {
                        for (tap = OS_TAPS - 1; tap > 0; tap--) inLine[tap] = inLine[tap - 1];
                        inLine[0] = b[i];
                        for (p = 0; p < OS; p++) {
                            var ph = phases[p], up = 0;
                            for (tap = 0; tap < OS_TAPS; tap++) up += inLine[tap] * ph[tap];
                            hi[hiPos] = Math.tanh(k * up) / t;              // насыщение на 4× сетке
                            hiPos = (hiPos + 1) % OS_LEN;
                        }
                        var acc = 0, idx = hiPos;
                        for (tap = 0; tap < OS_LEN; tap++) {
                            idx = idx === 0 ? OS_LEN - 1 : idx - 1;
                            acc += hi[idx] * h[tap];
                        }
                        var x0 = d[i], dly = s.dry[s.dryPos];
                        s.dry[s.dryPos] = x0; s.dryPos = (s.dryPos + 1) % SAT_DELAY;
                        d[i] = dly + mix * acc;
                    }
                    s.hiPos = hiPos;
                }
                return chans;
            }
        };
    }

    /* Per-sample core of compressor-worklet.js (Giannoulis et al. feed-forward,
       log-domain smooth-branched ballistics), with state carried across chunks
       instead of across 128-sample quanta — the algorithm is strictly
       sequential, so the port is bit-identical by construction and asserted
       against the worklet source in mastering-stream-tone.mjs. Only the
       metering and the (default-off) upward stage are omitted — neither
       touches the audio path at the chain's settings. */
    var DB_FLOOR = -120, LIN_FLOOR = 1e-6, LN9 = 2.1972245773362196;
    function staticOutputDb(xg, T, R, W) {
        var over = 2 * (xg - T);
        if (W > 0 && over > -W && over < W) {
            var d = xg - T + W / 2;
            return xg + (1 / R - 1) * (d * d) / (2 * W);
        }
        if (over <= -W) return xg;
        return T + (xg - T) / R;
    }
    function makeGlueCompressor(sr, nCh, p) {
        var T = p.thresholdDb, R = Math.max(1, p.ratio), W = Math.max(0, p.kneeDb);
        var makeupDb = p.makeupDb || 0;
        var lookahead = Math.max(0, Math.floor(p.lookaheadSamples || 0));
        var ringLen = lookahead + 1;
        var ring = [];
        for (var c0 = 0; c0 < nCh; c0++) ring.push(new Float32Array(ringLen));
        var ringPos = 0, gState = 0;
        var aA = p.attackMs <= 0 ? 0 : Math.exp(-LN9 / ((p.attackMs / 1000) * sr));
        var aR = p.releaseMs <= 0 ? 0 : Math.exp(-LN9 / ((p.releaseMs / 1000) * sr));
        // Detector high-pass (~85 Hz, worklet default) on the mono side-chain.
        var hz = (typeof p.sidechainHpfHz === 'number') ? p.sidechainHpfHz : 85;
        var hp = null;
        if (hz > 0 && hz < sr / 2) {
            var w0 = 2 * Math.PI * hz / sr, cw = Math.cos(w0), sw = Math.sin(w0);
            var alpha = sw / (2 * 0.70710678);
            var b0 = (1 + cw) / 2, b1 = -(1 + cw), b2 = (1 + cw) / 2;
            var a0 = 1 + alpha, a1 = -2 * cw, a2 = 1 - alpha;
            hp = { b0: b0 / a0, b1: b1 / a0, b2: b2 / a0, a1: a1 / a0, a2: a2 / a0, x1: 0, x2: 0, y1: 0, y2: 0 };
        }
        var invCh = 1 / nCh, hasLookahead = lookahead > 0;
        return {
            latency: lookahead,   // выход задержан ровно на упреждение (кольцо lookahead+1)
            process: function (chans) {
                var n = chans[0].length;
                for (var i = 0; i < n; i++) {
                    var sum = 0, c;
                    for (c = 0; c < nCh; c++) sum += chans[c][i];
                    var side = sum * invCh;
                    if (hp) {
                        var y = hp.b0 * side + hp.b1 * hp.x1 + hp.b2 * hp.x2 - hp.a1 * hp.y1 - hp.a2 * hp.y2;
                        hp.x2 = hp.x1; hp.x1 = side; hp.y2 = hp.y1; hp.y1 = y;
                        side = y;
                    }
                    var sc = side < 0 ? -side : side;
                    var xg = sc > LIN_FLOOR ? 20 * Math.log10(sc) : DB_FLOOR;
                    var gStatic = staticOutputDb(xg, T, R, W) - xg;
                    if (gStatic < gState) gState = aA * gState + (1 - aA) * gStatic;
                    else gState = aR * gState + (1 - aR) * gStatic;
                    var gainLin = Math.pow(10, (gState + makeupDb) / 20);
                    var wpos = ringPos;
                    var rpos = hasLookahead ? (wpos + 1) % ringLen : wpos;
                    for (c = 0; c < nCh; c++) {
                        var x = chans[c][i], delayed;
                        if (hasLookahead) { delayed = ring[c][rpos]; ring[c][wpos] = x; }
                        else delayed = x;
                        chans[c][i] = delayed * gainLin;
                    }
                    ringPos = (wpos + 1) % ringLen;
                }
                return chans;
            }
        };
    }

    /* Chain's msWiden with cross-chunk HPF state: side = (L−R)/2, high-passed
       at 120 Hz (lows stay mono), L += k·side, R −= k·side. Same recurrence
       and the same per-sample float32 stores as hpfInPlace, so the chunked
       output matches the buffered one bit for bit (asserted in
       mastering-stream-tone.mjs). Reference side-band EQ is not ported: the
       streaming lane does not do reference matching in v1. */
    function makeWidthStage(sr, w) {
        var fc = 120, Q = 0.7071;
        var w0 = 2 * Math.PI * fc / sr, cw = Math.cos(w0), sw = Math.sin(w0);
        var alpha = sw / (2 * Q);
        var b0 = (1 + cw) / 2, b1 = -(1 + cw), b2 = (1 + cw) / 2;
        var a0 = 1 + alpha, a1 = -2 * cw, a2 = 1 - alpha;
        b0 /= a0; b1 /= a0; b2 /= a0; a1 /= a0; a2 /= a0;
        var x1 = 0, x2 = 0, y1 = 0, y2 = 0;
        var k = w - 1;
        return {
            process: function (chans) {
                if (chans.length < 2) return chans;
                var L = chans[0], R = chans[1], n = L.length;
                var side = new Float32Array(n);
                for (var i = 0; i < n; i++) side[i] = (L[i] - R[i]) * 0.5;
                for (i = 0; i < n; i++) {
                    var x = side[i];
                    var y = b0 * x + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
                    x2 = x1; x1 = x; y2 = y1; y1 = y;
                    side[i] = y;
                }
                for (i = 0; i < n; i++) {
                    var d = k * side[i];
                    L[i] += d; R[i] -= d;
                }
                return chans;
            }
        };
    }

    // ── waveform peaks, O(bins) ───────────────────────────────────────
    function makePeaksCollector(totalSamplesEst, bins) {
        bins = bins || 1200;
        var spb = Math.max(1, Math.ceil((totalSamplesEst || 0) / bins) || 1);
        var out = new Float32Array(bins);
        var pos = 0;
        return {
            push: function (chans, n) {
                for (var c = 0; c < chans.length; c++) {
                    var d = chans[c], p = pos;
                    for (var i = 0; i < n; i++, p++) {
                        var b = (p / spb) | 0;
                        if (b >= out.length) {           // estimate was short — grow
                            var g = new Float32Array(Math.max(out.length * 2, b + 1));
                            g.set(out); out = g;
                        }
                        var v = d[i]; if (v < 0) v = -v;
                        if (v > out[b]) out[b] = v;
                    }
                }
                pos += n;
            },
            result: function () {
                var used = Math.min(out.length, Math.ceil(pos / spb) || 0);
                return out.subarray(0, used);
            },
            samplesPerBin: spb
        };
    }

    // ── chunked decode driver (one pass over the source) ──────────────
    async function decodePass(file, hdr, onChunk, onProgress, signal) {
        var I = deps();
        /* Децимация hi-res ПО ЯВНОМУ ВЫБОРУ пользователя: master() кладёт в
           hdr.nativeRate родную частоту файла, а hdr.sampleRate уже понижен —
           все потребители (тон, лимитер, метры, WAV-писатель) автоматически
           живут на эффективной частоте, декодер — на родной. Состояние FIR
           переносится через чанки (выход не зависит от нарезки — контракт
           node-теста stream-decimator). Хвост flush() доигрывается после
           прохода. Без nativeRate ветка мертва — поведение прежнее. */
        var _native = hdr.nativeRate || hdr.sampleRate;
        var _decim = _native > hdr.sampleRate ? Math.round(_native / hdr.sampleRate) : 1;
        if (_decim > 1) {
            if (!window.StreamDecimator) throw err('StreamDecimator not loaded');
            var _d = window.StreamDecimator.create(hdr.channels, _decim);
            var _downOn = onChunk;
            onChunk = async function (chans, n) {
                var o = _d.push(chans, n);
                if (o.frames) await _downOn(o.channels, o.frames);
            };
            var _flushTail = async function () {
                var t = _d.flush();
                if (t.frames) await _downOn(t.channels, t.frames);
            };
        }
        // декодеру — родная частота файла
        hdr = _decim > 1 ? Object.assign({}, hdr, { sampleRate: _native }) : hdr;
        /* ⚠️⚠️ Готовый буфер отдаём стадиям ТОЛЬКО КОПИЕЙ куска, никогда не
           представлением (subarray) на данные источника. Стадии этого лейна
           обрабатывают ВНУТРИ входного массива — из файловых веток им всегда
           прилетал свежий буфер декодера, поэтому право на запись было
           бесплатным и нигде не оговорено. Первая версия адаптера отдавала
           subarray «чтобы не копировать» и МОЛЧА портила исходник: A/B сравнивал
           обработанное с обработанным, повторный прогон шёл по уже сжёванному
           звуку, а сверка с буферной цепочкой показывала +4.4 дБ расхождения
           вместо −60. Замерено mm-inplace-scratch.
           Черновик выделяется ОДИН раз и переиспользуется — 0.5 МБ на стерео. */
        if (hdr.kind === 'buffer') {
            var buf = hdr.buffer, total = buf.length, nCh = buf.numberOfChannels;
            var srcChans = [];
            for (var c = 0; c < nCh; c++) srcChans.push(buf.getChannelData(c));
            var CH = 65536, scratch = [];
            for (var s = 0; s < nCh; s++) scratch.push(new Float32Array(CH));
            for (var off = 0; off < total; off += CH) {
                abortCheck(signal);
                var n = Math.min(CH, total - off), view = [];
                for (var k = 0; k < nCh; k++) {
                    scratch[k].set(srcChans[k].subarray(off, off + n));
                    view.push(n === CH ? scratch[k] : scratch[k].subarray(0, n));
                }
                await onChunk(view, n);
                if (onProgress) onProgress(Math.min(1, (off + n) / total));
                // Уступаем поток: без этого длинный трек держит вкладку намертво
                // и страница перестаёт рисовать прогресс — та же болезнь, от
                // которой лечит чанковый декод.
                if ((off / CH) % 16 === 15) await new Promise(function (r) { setTimeout(r, 0); });
            }
            if (_decim > 1) await _flushTail();
            return;
        }
        if (hdr.kind === 'wav') {
            await I.decodeWavStream(file, {
                samplesPerChunk: 65536,
                onChunk: async function (s) {
                    abortCheck(signal);
                    await onChunk(s.channelData, s.numberOfFrames);
                },
                onProgress: onProgress
            });
        } else {
            if (!window.FlacChunkedDecode) throw err('FlacChunkedDecode not loaded');
            var Off = window.OfflineAudioContext || window.webkitOfflineAudioContext;
            if (!Off) throw err('OfflineAudioContext unavailable');
            // ⚠️ The context MUST be at the file's own rate: decodeAudioData
            // resamples to the context rate, and a hi-res master silently
            // degraded per chunk is exactly the bug class this lane exists
            // to avoid (reference_hires_audio_rate_bitdepth).
            var ctx = new Off(hdr.channels, 1, hdr.sampleRate);
            await window.FlacChunkedDecode.decodeChunks(file, ctx, {
                chunkSec: 10,
                signal: signal,
                onChunk: async function (ab) {
                    abortCheck(signal);
                    var chans = [];
                    for (var c = 0; c < ab.numberOfChannels; c++) chans.push(ab.getChannelData(c));
                    await onChunk(chans, ab.length);
                },
                onProgress: onProgress
            });
        }
        if (_decim > 1) await _flushTail();
    }

    /* Цепь тона, выровненная во времени. Она задерживает звук на tone.latency
       отсчётов (упреждение компрессора, фильтры насыщения): столько отрезаем с
       головы и столько же прокачиваем тишиной в конце — иначе мастер начинался
       с 3 мс тишины и терял свои последние 3 мс (буферный путь делает то же в
       renderToneComp). Один код на ЗАМЕР громкости (проход 2) и на РЕНДЕР:
       меряй они разный сигнал, усиление разошлось бы на 0.01 дБ (замер 09.10). */
    function alignedTone(tone, nCh, sink) {
        var skip = tone.latency || 0;
        async function feed(chans, n) {
            tone.process(chans);
            if (skip > 0) {
                var drop = Math.min(skip, n); skip -= drop;
                if (drop === n) return;
                // Новый массив представлений: массив декодера переиспользуется
                // между кусками, подменять в нём каналы нельзя.
                var view = [];
                for (var dc = 0; dc < chans.length; dc++) view.push(chans[dc].subarray(drop));
                chans = view; n -= drop;
            }
            await sink(chans, n);
        }
        feed.finish = async function () {
            if (!tone.latency) return;
            var zeros = [];
            for (var zc = 0; zc < nCh; zc++) zeros.push(new Float32Array(tone.latency));
            await feed(zeros, tone.latency);
        };
        return feed;
    }

    // ── render pass: tone → pre-gain+limit → meter → WAV sink ─────────
    async function renderPass(file, hdr, styleObj, rmsDb, cfg) {
        var I = deps();
        var sink = await window.OutputSink.create({ mime: 'audio/wav', name: cfg.sinkName });
        try {
            var writer = await I.makeWavWriter(sink, hdr.sampleRate, hdr.channels, cfg.bits);
            var tone = buildToneChain(hdr.sampleRate, hdr.channels, styleObj, rmsDb);
            var lim = window.MasteringLimiter.createStreamLimiter(hdr.sampleRate, hdr.channels, {
                ceilingDb: cfg.ceilingDb, preGainDb: cfg.preGainDb
            });
            var meter = window.LoudnessR128.createIntegratedMeter(hdr.sampleRate, hdr.channels);
            var peaks = makePeaksCollector(hdr.totalSamples || Math.round((hdr.durationSec || 600) * hdr.sampleRate));
            var scale = cfg.postScale || 1;
            async function emit(blocks) {
                for (var b = 0; b < blocks.length; b++) {
                    var chans = blocks[b];
                    if (scale !== 1) {
                        for (var c = 0; c < chans.length; c++) {
                            var d = chans[c];
                            for (var i = 0; i < d.length; i++) d[i] *= scale;
                        }
                    }
                    var n = chans[0].length;
                    meter.push(chans, n);
                    peaks.push(chans, n);
                    await writer.writeChunk(chans);
                }
            }
            var feed = alignedTone(tone, hdr.channels, async function (chans, n) { await emit(lim.push(chans, n)); });
            await decodePass(file, hdr, feed, cfg.onProgress, cfg.signal);
            await feed.finish();
            var tail = lim.flush();
            await emit(tail.blocks);
            var blob = await writer.finalize();
            // `sink` rides along so a superseded render (correction pass,
            // verify trim) can delete its OPFS file at once instead of
            // leaving a full-length orphan until the TTL sweep — on a 2-hour
            // master that orphan is over a gigabyte.
            return { blob: blob, maxGrDb: tail.maxGrDb, after: meter.finish(), peaks: peaks, sink: sink };
        } catch (e) {
            try { await sink.cleanup(); } catch (_) {}
            throw e;
        }
    }

    // Cheap output-only trim: rescale the WRITTEN WAV by a scalar, no source
    // decode, no tone, no limiter — used when the verified true peak of the
    // render tops the promised ceiling (gain modulation can create
    // inter-sample peaks the input-side bound never saw; the buffered chain
    // learned this on 2026-07-26). Re-quantization adds ≤0.5 LSB once, only
    // on this rare path. Returns the new blob plus honest re-measured
    // metrics (the meter runs over the exact floats being written).
    async function rescaleWavBlob(blob, hdr, bits, lin, sinkName) {
        var I = deps();
        var sink = await window.OutputSink.create({ mime: 'audio/wav', name: sinkName });
        try {
            var writer = await I.makeWavWriter(sink, hdr.sampleRate, hdr.channels, bits);
            var meter = window.LoudnessR128.createIntegratedMeter(hdr.sampleRate, hdr.channels);
            var peaks = makePeaksCollector(hdr.totalSamples || Math.round((hdr.durationSec || 600) * hdr.sampleRate));
            var bytesPerSample = bits / 8, nCh = hdr.channels;
            var bytesPerFrame = bytesPerSample * nCh;
            var FRAMES = 65536;
            // Our writer's header is 44 bytes up to stereo and 68 above it
            // (OutputSink.wavHeader) — the data chunk is found, not assumed.
            var head = new DataView(await blob.slice(0, 256).arrayBuffer()), pos = 12, end = blob.size;
            while (pos + 8 <= head.byteLength) {
                var cid = String.fromCharCode(head.getUint8(pos), head.getUint8(pos + 1), head.getUint8(pos + 2), head.getUint8(pos + 3));
                var csz = head.getUint32(pos + 4, true);
                if (cid === 'data') { pos += 8; break; }
                pos += 8 + csz + (csz & 1);
            }
            while (pos < end) {
                var sliceEnd = Math.min(pos + FRAMES * bytesPerFrame, end);
                var buf = new Uint8Array(await blob.slice(pos, sliceEnd).arrayBuffer());
                var dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
                var nFrames = Math.floor(buf.length / bytesPerFrame);
                if (!nFrames) break;
                var chans = [];
                for (var c = 0; c < nCh; c++) chans.push(new Float32Array(nFrames));
                for (var f = 0; f < nFrames; f++) {
                    for (var c2 = 0; c2 < nCh; c2++) {
                        var off = f * bytesPerFrame + c2 * bytesPerSample, v;
                        if (bits === 16) v = dv.getInt16(off, true) / 32768;
                        else if (bits === 24) {
                            var raw = buf[off] | (buf[off + 1] << 8) | (buf[off + 2] << 16);
                            if (raw & 0x800000) raw |= 0xFF000000;
                            v = raw / 8388608;
                        } else v = dv.getFloat32(off, true);
                        chans[c2][f] = v * lin;
                    }
                }
                meter.push(chans, nFrames);
                peaks.push(chans, nFrames);
                await writer.writeChunk(chans);
                pos = sliceEnd;
            }
            var out = await writer.finalize();
            return { blob: out, after: meter.finish(), peaks: peaks, sink: sink };
        } catch (e) {
            try { await sink.cleanup(); } catch (_) {}
            throw e;
        }
    }

    // ── the whole lane ────────────────────────────────────────────────
    async function master(file, opts) {
        opts = opts || {};
        deps();
        var stage = opts.onStage || function () {};
        var prog = opts.onProgress || function () {};
        var signal = opts.signal || null;
        var targetLufs = (typeof opts.targetLufs === 'number') ? opts.targetLufs : -14;
        var ceilingDb = (typeof opts.ceilingDb === 'number') ? opts.ceilingDb : -1.0;
        var bits = (opts.bits === 24 || opts.bits === 32) ? opts.bits : 16;
        var styleKey = opts.style || 'transparent';
        // «Авто» в потоковой дорожке v1 НЕТ: тональное решение принимается по
        // спектру ВСЕГО трека, а здесь его целиком нет по построению. Молча
        // подменять авто на «прозрачный» нельзя — человек выбрал одно, получил
        // бы другое, — поэтому вызывающая сторона предупреждает, а мы честно
        // работаем как «прозрачный» и говорим об этом в результате.
        var autoAsked = styleKey === 'auto';
        if (autoAsked) styleKey = 'transparent';
        var styleObj = Object.assign({}, window.MasteringChain.STYLES[styleKey] || window.MasteringChain.STYLES.transparent);
        if (typeof opts.width === 'number') styleObj.width = opts.width;
        if (opts.tone) styleObj._tone = opts.tone;
        // Только полосы АЧХ. Покривлённая по частоте стереоширина (sideBands)
        // здесь не поддержана, поэтому вызывающая сторона обязана свести её к
        // одному множителю opts.width — молча принять и не применить хуже, чем
        // не принять вовсе.
        if (opts.refBands && opts.refBands.length) styleObj._refBands = opts.refBands;

        var hdr = await probe(file);
        if (!(hdr.sampleRate > 0) || !(hdr.channels > 0)) throw err('unreadable header');
        /* Тембр по ходу трека: рампе нужна только длительность — статистика всего
           трека ей не нужна, поэтому потоковый путь применяет её так же, как
           буферный (а не отказывает тостом, как динамическому EQ). Смещение под
           подгонку — по тем же правилам, что в MasteringChain. */
        if (opts.drift && window.MasteringTonalDrift) {
            var _dr = window.MasteringTonalDrift.resolve(opts.drift, hdr.durationSec);
            if (_dr && styleObj._refBands && styleObj._refBands.length) _dr = window.MasteringTonalDrift.withOffsets(_dr, hdr.durationSec);
            styleObj._drift = _dr;
        }

        /* Обработка hi-res на пониженной частоте — ТОЛЬКО по явному выбору
           (opts.targetRate от UI-переключателя; никакой тихой деградации).
           Точные семейные пары через целый фактор 2/4/8: 384/192→96,
           352.8/176.4→88.2. Всё, что ниже hdr, живёт на эффективной частоте;
           декодеру decodePass отдаёт родную через hdr.nativeRate. Выигрыш —
           фактор× на КАЖДОМ из 3-4 проходов лейна и фактор× по памяти чанков
           (замер 09.08: 384k = 113 с счёта на 60 с звука при CPU 4×). */
        if (opts.targetRate && window.StreamDecimator && hdr.sampleRate > opts.targetRate) {
            var _f = hdr.sampleRate / opts.targetRate;
            if (_f === 2 || _f === 4 || _f === 8) {
                hdr = Object.assign({}, hdr, {
                    nativeRate: hdr.sampleRate,
                    sampleRate: opts.targetRate,
                    totalSamples: hdr.totalSamples ? Math.floor(hdr.totalSamples / _f) : hdr.totalSamples
                });
            }
        }

        function window01(from, to) {
            return function (r) { try { prog(from + (to - from) * Math.max(0, Math.min(1, r || 0))); } catch (_) {} };
        }

        // Pass 1 — raw "before" metrics. Also the source of the RMS the
        // compressor threshold hangs off, same as the chain's analyze stage.
        stage('analyze'); prog(0.02);
        var beforeMeter = window.LoudnessR128.createIntegratedMeter(hdr.sampleRate, hdr.channels);
        var beforePeaks = makePeaksCollector(hdr.totalSamples || Math.round((hdr.durationSec || 600) * hdr.sampleRate));
        await decodePass(file, hdr, async function (chans, n) {
            beforeMeter.push(chans, n);
            beforePeaks.push(chans, n);
        }, window01(0.02, 0.24), signal);
        var before = beforeMeter.finish();

        // Pass 2 — tone chain, exact loudness of what the limiter will see.
        stage('tone'); prog(0.26);
        var toneMeter = window.LoudnessR128.createIntegratedMeter(hdr.sampleRate, hdr.channels);
        var tone2 = buildToneChain(hdr.sampleRate, hdr.channels, styleObj, before.rmsDb);
        var feed2 = alignedTone(tone2, hdr.channels, async function (chans, n) { toneMeter.push(chans, n); });
        await decodePass(file, hdr, feed2, window01(0.26, 0.5), signal);
        await feed2.finish();
        var lufsA = toneMeter.finish().integratedLUFS;
        if (!isFinite(lufsA)) lufsA = targetLufs;          // silence guard, as in the chain

        // Pass 3 — render at the exact measured gain.
        stage('limit'); prog(0.52);
        var appliedGain = targetLufs - lufsA;
        var iterations = 1;
        var r = await renderPass(file, hdr, styleObj, before.rmsDb, {
            bits: bits, ceilingDb: ceilingDb, preGainDb: appliedGain,
            sinkName: 'mm-stream', onProgress: window01(0.52, 0.9), signal: signal
        });
        var lastErr = targetLufs - r.after.integratedLUFS;

        // Rare pass 4 — the limiter ate more loudness than the pre-gain
        // predicted (or the source gated oddly): one full correction render,
        // same etalon rule as the chain — never chase the number into
        // over-compression (maxGr ≥ 14 stops the chase, reported honestly).
        if (isFinite(lastErr) && Math.abs(lastErr) > 0.5 && r.maxGrDb < 14) {
            stage('limit');
            iterations = 2;
            appliedGain += lastErr;
            var superseded = r.sink;
            r = await renderPass(file, hdr, styleObj, before.rmsDb, {
                bits: bits, ceilingDb: ceilingDb, preGainDb: appliedGain,
                sinkName: 'mm-stream', onProgress: window01(0.6, 0.92), signal: signal
            });
            try { if (superseded) await superseded.cleanup(); } catch (_) {}
            lastErr = targetLufs - r.after.integratedLUFS;
        }

        // Verify the promise on the OUTPUT with the same meter we report:
        // if the measured true peak tops (ceiling − 0.15 dB of external-meter
        // slack), trim the written file by the exact excess — output-only.
        stage('measure'); prog(0.94);
        var verifyAt = ceilingDb - 0.15;
        if (isFinite(r.after.truePeakDb) && r.after.truePeakDb > verifyAt) {
            var trimDb = verifyAt - r.after.truePeakDb - 0.1;
            var rr = await rescaleWavBlob(r.blob, hdr, bits, dbToLin(trimDb), 'mm-stream-trim');
            try { if (r.sink) await r.sink.cleanup(); } catch (_) {}
            r = { blob: rr.blob, maxGrDb: r.maxGrDb, after: rr.after, peaks: rr.peaks };
            lastErr = targetLufs - r.after.integratedLUFS;
        }
        prog(1);

        return {
            blob: r.blob,
            before: before,
            after: r.after,
            appliedGainDb: appliedGain,
            residualErrLu: isFinite(lastErr) ? lastErr : 0,
            overLimited: isFinite(lastErr) && Math.abs(lastErr) > 0.5,
            maxGrDb: r.maxGrDb,
            iterations: iterations,
            targetLufs: targetLufs,
            ceilingDb: ceilingDb,
            style: styleKey,
            autoUnavailable: autoAsked,
            drift: styleObj._drift || null,
            peaks: {
                before: beforePeaks.result(),
                after: r.peaks.result(),
                samplesPerBin: beforePeaks.samplesPerBin
            },
            sampleRate: hdr.sampleRate,
            channels: hdr.channels,
            durationSec: hdr.durationSec,
            bits: bits,
            mode: 'streaming'
        };
    }

    window.MasteringStream = {
        canHandle: canHandle,
        probe: probe,
        master: master,
        // exposed for unit tests
        _internal: {
            buildToneChain: buildToneChain,
            makeGlueCompressor: makeGlueCompressor,
            makeWidthStage: makeWidthStage,
            makePeaksCollector: makePeaksCollector,
            makeSaturator: makeSaturator,
            webAudioHpLpQ: webAudioHpLpQ
        },
        version: '1.0.0'
    };

    if (typeof window.__drainSharedModule === 'function') window.__drainSharedModule('MasteringStream');
})();
